import React, {
  useState,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useDeferredValue,
} from "react";
import { SocketContext } from "../../context/socket";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Message from "./Message";
import User from "./User";
import { getUserFromLocalStorage } from "../../utils/localStorage";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import { API_URL } from "../../constants/constants";
import moment from "moment";
import { setMessages } from "../../redux/messageSlice";
import Images from "./Images";
import Files from "./Files";
import UserRead from "./UserRead";
import MessageTime from "./MessageTime";
import { showApiError } from "../../utils/apiError";
import { measureInvertedScroll } from "../../utils/invertedScroll";

const MY_MESSAGE = 1;
const OTHER_MESSAGE = 2;

export default function MessageBox() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const socket = useContext(SocketContext);
  const task = useSelector((state) => state.current);
  const [user, setUser] = useState({});
  // The avatars shown beside "seen by". Nothing has filled this since the code
  // that did was commented out, so every UserRead below is handed undefined and
  // falls back to its own placeholder. Kept as it is rather than quietly
  // changed: making the avatars appear is a decision about the feature, not
  // tidying.
  const [users, setUsers] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const messageBoxRef = useRef(null);

  // Whether the reader is at the newest message. New arrivals only pull the
  // view down when they already were: reading older messages should not be
  // interrupted by somebody else typing.
  const stickToBottom = useRef(true);
  const lastTaskId = useRef(null);

  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Set User
  useEffect(() => {
    const user = getUserFromLocalStorage();
    setUser(user);
  }, []);

  const messages = useSelector((state) => state.message);
  const finalMessages = useDeferredValue(messages);

  // Reload message when task id is changed
  useEffect(() => {
    async function fetchData() {
      try {
        const res = await api.get(`${API_URL}/api/v1/message/all/${task._id}`);
        // The route answers with a page now ({messages, hasMore}), not the whole
        // conversation as a bare array. Accept both shapes so this build also
        // works against an older API.
        const messages = Array.isArray(res.data)
          ? res.data
          : res.data?.messages || [];
        setHasOlderMessages(
          Array.isArray(res.data) ? false : !!res.data?.hasMore,
        );
        dispatch(setMessages(messages));
      } catch (err) {
        if (err.response.status === 401) {
          navigate("/");
        }
        setIsLoading(false); // Set isLoading to false in case of an error
        showApiError(err);
      }
      socket.emit("join-chat", task._id);
    }
    fetchData();
    // Only the id. This also depended on task.users, and that is a new array
    // every time redux replaces the task object, which happens while a
    // conversation is being opened. So the conversation was fetched twice, and
    // the list was drawn twice, for one task.
  }, [task._id]);

  // The route answers with the newest page. Everything before it is fetched on
  // demand, oldest-first cursor style, when the reader reaches the top.
  const loadOlderMessages = async () => {
    if (loadingOlder || !hasOlderMessages) return;
    const oldest = finalMessages[0];
    if (!oldest?._id) return;

    setLoadingOlder(true);
    try {
      const res = await api.get(
        `${API_URL}/api/v1/message/all/${task._id}?before=${oldest._id}`,
      );
      const older = Array.isArray(res.data)
        ? res.data
        : res.data?.messages || [];
      setHasOlderMessages(
        Array.isArray(res.data) ? false : !!res.data?.hasMore,
      );
      // No scroll correction needed: in an inverted list these are appended to
      // the end of the DOM, which is above the reader and does not move them.
      if (older.length) dispatch(setMessages([...older, ...finalMessages]));
    } catch (err) {
      toast.error("Could not load older messages");
    } finally {
      setLoadingOlder(false);
    }
  };

  // In an inverted container, scroll position 0 is the bottom. The measuring
  // lives in utils/invertedScroll, where the disagreement between engines over
  // the sign of scrollTop is dealt with and tested.
  const onMessagesScroll = () => {
    const position = measureInvertedScroll(messageBoxRef.current);
    if (!position) return;
    stickToBottom.current = position.atBottom;
    if (position.nearTop) loadOlderMessages();
  };

  // Placing the view, before the browser paints.
  //
  // This waited 300 ms after the messages rendered and then scrolled, so every
  // time a conversation opened the reader saw the top of it and then a jump.
  // useLayoutEffect runs before the paint, so the first thing on screen is
  // already the newest message.
  useLayoutEffect(() => {
    // Almost nothing left to do. The list is inverted, so zero is the bottom
    // and that is where a scroll container starts: a conversation is already
    // showing its newest message before the first paint, with no scrolling to
    // see. Older messages land at the far end of the DOM and cannot move the
    // reader, and a new message arriving while they are up in the history
    // cannot either, so the measuring and correcting that used to live here is
    // gone with it.
    //
    // The one case the browser cannot know about is a different conversation
    // being opened in the same container, which keeps whatever position the
    // previous one was left at.
    if (lastTaskId.current === task._id) return;
    lastTaskId.current = task._id;
    stickToBottom.current = true;
    if (messageBoxRef.current) messageBoxRef.current.scrollTop = 0;
  }, [finalMessages, task._id, isLoading]);

  useEffect(() => {
    if (finalMessages.length > 0) setIsLoading(false);
  }, [finalMessages]);
  return (
    // column-reverse, so the first child below is drawn at the bottom and the
    // last at the top. The order things are written here is therefore the
    // reverse of the order they appear on screen.
    <div
      ref={messageBoxRef}
      className="message-list"
      onScroll={onMessagesScroll}
    >
      {isLoading ? "loading....." : ""}
      {!isLoading &&
        finalMessages &&
        finalMessages
          .map((message, index) => {
            return (
              // Keyed by the message, not by its position: keying by index makes
              // React reuse a bubble for a different message when older ones are
              // prepended, so the wrong text keeps the wrong state.
              <div key={message._id || index} data-mid={message._id}>
                {(index == 0 ||
                  moment(finalMessages[index - 1].createdAt).format(
                    "MMM Do YY",
                  ) !=
                    moment(finalMessages[index].createdAt).format(
                      "MMM Do YY",
                    )) && (
                  <div className="d-flex justify-content-between m-2">
                    <div className="w-100">
                      <hr />
                    </div>
                    <div className="text-center" style={{ width: "400px" }}>
                      {moment(message.createdAt).format("LL")}
                    </div>
                    <div className="w-100">
                      <hr />
                    </div>
                  </div>
                )}
                {(index == 0 ||
                  finalMessages[index - 1].sender._id != message.sender._id) &&
                message.sender._id != user._id ? (
                  <User
                    avatar={message.sender.avatar}
                    user={message.sender.username}
                  />
                ) : (
                  <></>
                )}
                {/* {(index == 0 ||
                messages[index - 1].sender._id != message.sender._id ||
                new Date(message.createdAt) -
                  new Date(messages[index - 1].createdAt) >
                  300000) &&
              message.sender._id != user._id ? (
                <User
                  avatar={message.sender.avatar}
                  user={message.sender.username}
                />
              ) : (
                <></>
              )} */}
                {index == 0 ||
                finalMessages[index - 1].sender._id != message.sender._id ||
                new Date(message.createdAt) -
                  new Date(finalMessages[index - 1].createdAt) >
                  300000 ? (
                  <MessageTime
                    time={new Date(message.createdAt).toLocaleTimeString()}
                    type={
                      message.sender._id == user._id
                        ? MY_MESSAGE
                        : OTHER_MESSAGE
                    }
                  />
                ) : (
                  <></>
                )}
                {message.message ? (
                  <Message
                    id={message._id}
                    message={message.message}
                    type={
                      message.sender._id == user._id
                        ? MY_MESSAGE
                        : OTHER_MESSAGE
                    }
                    deleted={message.message_deleted}
                    replied={message.replied}
                    edited={message.edited}
                    replymessage={
                      message.replied
                        ? message.replymessage
                        : {
                            id: "",
                            message: "",
                          }
                    }
                  />
                ) : (
                  <></>
                )}

                {message.images.length > 0 ? (
                  <Images
                    images={message.images}
                    type={
                      message.sender._id == user._id
                        ? MY_MESSAGE
                        : OTHER_MESSAGE
                    }
                  />
                ) : (
                  <></>
                )}
                {message.files.length > 0 ? (
                  <Files
                    files={message.files}
                    type={
                      message.sender._id == user._id
                        ? MY_MESSAGE
                        : OTHER_MESSAGE
                    }
                  />
                ) : (
                  <></>
                )}
                <div className="d-flex justify-content-end">
                  {task.unread.map((unread) =>
                    user._id != unread.user &&
                    index == messages.length - unread.number - 1 ? (
                      <UserRead image={users[unread.user]} />
                    ) : (
                      <></>
                    ),
                  )}
                </div>
              </div>
            );
          })
          .reverse()}

      {/* Visually the top of the list, because of column-reverse. */}
      {loadingOlder && (
        <div className="text-center m-2" style={{ opacity: 0.6 }}>
          loading older messages…
        </div>
      )}
    </div>
  );
}
